import { SLA_TONE_DANGER_BELOW_PERCENT, SLA_TONE_WARNING_BELOW_PERCENT, type SlaState } from "@sagot/constants";
import type { ReactNode } from "react";
import { cssVars } from "../../css-vars";
import type { IconName } from "../../icons";
import { cn } from "../../lib/cn";
import { Icon } from "../icon";
import { Tooltip } from "../tooltip";

/** What the line looks like: the server's state refined by the share of the window left. */
export type SlaDisplay = "ok" | "warning" | "danger" | "breached" | "met" | "paused" | "none";

const PERCENT_MAX = 100;

const DISPLAY_SPEC: Readonly<Record<SlaDisplay, { icon: IconName; ink: string; severity: number }>> = {
  breached: { icon: "alert-octagon", ink: "text-danger", severity: 5 },
  danger: { icon: "alert-triangle", ink: "text-danger", severity: 4 },
  warning: { icon: "alert-triangle", ink: "text-warning", severity: 3 },
  paused: { icon: "pause-circle", ink: "text-fg-muted", severity: 2 },
  ok: { icon: "clock", ink: "text-fg-muted", severity: 1 },
  met: { icon: "check-circle", ink: "text-success", severity: 0 },
  none: { icon: "minus", ink: "text-fg-muted", severity: 0 },
};

/**
 * Tone by share of the window left, as the admin `sla.ts` does: below 35 % danger, below 60 % warning, otherwise calm.
 * A `warning` state from the server (its own warn-at percentage) never reads calmer than a warning.
 */
export function resolveSlaDisplay(state: SlaState, percentLeft?: number | null): SlaDisplay {
  if (state === "n/a") return "none";
  if (state === "breached" || state === "met" || state === "paused") return state;
  const byPercent: SlaDisplay =
    percentLeft == null ? "ok" : percentLeft < SLA_TONE_DANGER_BELOW_PERCENT ? "danger" : percentLeft < SLA_TONE_WARNING_BELOW_PERCENT ? "warning" : "ok";
  return state === "warning" && byPercent === "ok" ? "warning" : byPercent;
}

interface SlaSeverityInput {
  state: SlaState;
  percentLeft?: number | null;
}

/** The most urgent line of a ticket (list cell: one chip). Ties keep the first. */
export function mostUrgentSla<T extends SlaSeverityInput>(lines: readonly T[]): T | undefined {
  let best: T | undefined;
  let bestSeverity = -1;
  for (const line of lines) {
    const severity = DISPLAY_SPEC[resolveSlaDisplay(line.state, line.percentLeft)].severity;
    if (severity > bestSeverity) {
      best = line;
      bestSeverity = severity;
    }
  }
  return best;
}

export interface SlaChipProps {
  state: SlaState;
  /** Localised line, e.g. "Resolution due Thu 08:57". */
  text: string;
  /** Extra in brackets, e.g. "43 min". */
  detail?: string;
  /** 0..100 share of the window still left; draws the bar and sets the warning/danger tone. */
  percentLeft?: number | null;
  /** Screen-reader text for the bar, e.g. "31% of the time left". */
  percentLabel?: string;
  /** Explains the target and the clock (opens on hover and on keyboard focus). */
  tooltip?: ReactNode;
  className?: string;
}

function clampPercent(value: number): number {
  return Math.min(PERCENT_MAX, Math.max(0, Math.round(value)));
}

/** One SLA line: icon + text (+ detail) in the tone of the state, with a remaining-time bar when the share is known. */
export function SlaChip({ state, text, detail, percentLeft, percentLabel, tooltip, className }: SlaChipProps) {
  const display = resolveSlaDisplay(state, percentLeft);
  const spec = DISPLAY_SPEC[display];
  const showBar = percentLeft != null && (display === "ok" || display === "warning" || display === "danger");
  const line = (
    <span className={cn("inline-flex min-w-32 flex-col gap-1", spec.ink, className)} data-state={display} data-sla-state={state}>
      <span className="flex items-center gap-1.5 text-xs font-bold leading-tight [&>svg]:size-3.5">
        <Icon name={spec.icon} />
        <span>
          {text}
          {detail ? <span className="font-medium"> ({detail})</span> : null}
        </span>
      </span>
      {showBar ? (
        <>
          <span className="block h-1 overflow-hidden rounded-pill bg-sunken" aria-hidden="true" data-sla-bar="true">
            <span className="block h-full w-[var(--geo-width)] rounded-[inherit] bg-current" style={cssVars({ "--geo-width": `${clampPercent(percentLeft)}%` })} />
          </span>
          {percentLabel ? <span className="sr-only">{percentLabel}</span> : null}
        </>
      ) : null}
    </span>
  );
  if (!tooltip) return line;
  return (
    <Tooltip content={tooltip}>
      {/* The trigger must take focus so keyboard users read the explanation as well (hover alone would hide it). */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
      <span role="group" tabIndex={0} className="inline-flex rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
        {line}
      </span>
    </Tooltip>
  );
}
